import { faDotCircle } from "@fortawesome/free-regular-svg-icons/faDotCircle";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faSave } from "@fortawesome/free-solid-svg-icons/faSave";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { Switch, Tooltip } from "../../../../utils/local-antd";
import { TFunction } from "i18next";
import React, { useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "../../../../components/Button";
import { Input } from "../../../../components/Input";
import { Spacing } from "../../../../theme/Spacing";
import { CreateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/CreateModuleDefinitionDto";
import { UpdateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/UpdateModuleDefinitionDto";
import { InputTypes } from "../../../../model/form/types/InputTypes.enum";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { Modal } from "../../../../components/modal/Modal";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import isNumber from "lodash/isNumber";
import { InputConfigDate } from "./settings/InputConfigDate";

const validateOptionName = (
  options: string[],
  t: TFunction,
  value?: string,
) => {
  if (value && options.includes(value)) {
    return t("forms.create.step.2.item.duplicated");
  }

  return undefined;
};

const SaveButton = styled(Button)`
  flex-grow: 1;
  border: ${({ theme }) => `1px solid ${theme.main}`};

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const StyledButton = styled(Button)`
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  padding: 0;
  margin: 0;
  margin-bottom: auto;
  height: 2.3rem;
`;

const OptionInput = styled(Input)`
  html[dir="rtl"] & {
    direction: rtl;
  }
  html[dir="ltr"] & {
    direction: ltr;
  }

  ${({ readOnly }) =>
    readOnly
      ? `
    border-color: transparent !important;
  `
      : ""}
`;

const SaveOptionButton = styled(StyledButton)`
  margin-inline-start: ${Spacing.s};
`;

const StyledButtonWithOpacity = styled(Button)<{ $selected?: boolean }>`
  background-color: transparent;
  padding: 0;
  margin: 0;
  margin-bottom: auto;
  height: 2.3rem;
  opacity: ${({ $selected }) => ($selected ? 1 : 0.3)};
`;

interface ModuleDefinitionCreateEditUpdateInputModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
  inputIndex: number;
}

export const ModuleDefinitionCreateEditUpdateInputModal: React.FC<
  ModuleDefinitionCreateEditUpdateInputModalProps
> = ({ isOpen = false, onClose, inputIndex }) => {
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(
    null,
  );

  const { control, getValues, watch, setValue } = useFormContext<
    Partial<CreateModuleDefinitionDto | UpdateModuleDefinitionDto> & {
      optionName?: string;
    }
  >();

  const currentInput = getValues(`inputs.${inputIndex}`);
  const options = watch(`inputs.${inputIndex}.options`) || [];
  const optionName = watch("optionName") || "";

  const optionInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<HTMLInputElement[]>([]);

  const getDefaultMaxChar = () => {
    switch (getValues(`inputs.${inputIndex}.type`)?.toLowerCase()) {
      case InputTypes.NUMBER.toLowerCase():
        return Lengths.NUMBER;
      case InputTypes.PHONE.toLowerCase():
        return Lengths.PHONE;
      case InputTypes.EMAIL.toLowerCase():
        return Lengths.EMAIL;
      default:
        return Lengths.DESCRIPTION;
    }
  };
  const defaultMaxChar = getDefaultMaxChar();

  const { t } = useTranslation();

  const isRadio =
    currentInput?.type?.toLowerCase() === InputTypes.RADIO.toLowerCase();
  const isSelect =
    currentInput?.type?.toLowerCase() === InputTypes.SELECT.toLowerCase();
  const isDate =
    currentInput?.type?.toLowerCase() === InputTypes.DATE.toLowerCase();

  const onLocalClose = () => {
    onClose();
    setValue("optionName", "");
    setEditingOptionIndex(null);
  };

  const handleEditOptionClick = (
    index: number,
    setEditingOptionIndex: React.Dispatch<React.SetStateAction<number | null>>,
  ) => {
    setEditingOptionIndex(index);

    const ref = optionRefs.current[index];

    if (ref) {
      ref.focus();
    }
  };

  const setOptionAsDefault = (index: number) => {
    setValue(
      `inputs.${inputIndex}.selectInputConfig.defaultOptionIndex`,
      index,
      { shouldDirty: true },
    );
  };

  const addOption = () => {
    const trimmedOption = optionName.trim();

    if (
      trimmedOption.length &&
      options.length < 15 &&
      !options.includes(trimmedOption)
    ) {
      setValue(`inputs.${inputIndex}.options`, [...options, trimmedOption], {
        shouldDirty: true,
      });

      setValue("optionName", "");
    }
  };

  const removeOption = (index: number) => {
    const newOptions = [...options];

    newOptions.splice(index, 1);

    setValue(`inputs.${inputIndex}.options`, newOptions, {
      shouldDirty: true,
    });

    if (editingOptionIndex === index) {
      setEditingOptionIndex(null);
    }

    if (index === currentInput?.selectInputConfig?.defaultOptionIndex) {
      setValue(
        `inputs.${inputIndex}.selectInputConfig.defaultOptionIndex`,
        -1,
        { shouldDirty: true },
      );
    } else if (
      isNumber(currentInput?.selectInputConfig?.defaultOptionIndex) &&
      index < currentInput?.selectInputConfig?.defaultOptionIndex
    ) {
      // Removed option was before the default; shift default index left.
      setValue(
        `inputs.${inputIndex}.selectInputConfig.defaultOptionIndex`,
        currentInput.selectInputConfig.defaultOptionIndex - 1,
        { shouldDirty: true },
      );
    }
  };

  const onOptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && optionName.trim().length) {
      addOption();
      optionInputRef.current?.focus();
    }
  };

  return (
    <Modal
      destroyOnClose={true}
      zIndex={10000}
      key={inputIndex}
      open={isOpen}
      onClose={onLocalClose}
      title={t("forms.create.input_edit_modal.title")}
    >
      <Flex flexDirection="column" gap="m" width={"100%"}>
        <Controller
          key={`inputs.${inputIndex}.label`}
          control={control}
          rules={{
            required: {
              value: true,
              message: t("error.range", {
                name: t("common.input_label"),
                max: Lengths.NAME,
                min: 1,
              }),
            },
            minLength: {
              value: 1,
              message: t("error.length.more", {
                name: t("common.input_label"),
                length: 1,
              }),
            },
            maxLength: {
              value: Lengths.NAME,
              message: t("error.length.less", {
                name: t("common.input_label"),
                length: Lengths.NAME,
              }),
            },
          }}
          name={`inputs.${inputIndex}.label` as const}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value}
              onChange={onChange}
              title={t("common.input_label")}
              maxLength={Lengths.NAME}
              valid={!error}
              required
              errorMessage={error?.message}
            />
          )}
        />

        {![InputTypes.DATE, InputTypes.SELECT, InputTypes.RADIO].includes(
          currentInput?.type?.toLowerCase() as InputTypes,
        ) ? (
          <Controller
            key={`inputs.${inputIndex}.settings.maxChars`}
            control={control}
            name={`inputs.${inputIndex}.settings.maxChar`}
            rules={{
              required: {
                value: true,
                message: t("error.range", {
                  name: t("common.max_chars"),
                  max: defaultMaxChar,
                  min: Lengths.MIN_3,
                }),
              },
              max: {
                value: defaultMaxChar,
                message: t("error.range", {
                  name: t("common.max_chars"),
                  max: defaultMaxChar,
                  min: Lengths.MIN_3,
                }),
              },
              min: {
                value: Lengths.MIN_3,
                message: t("error.range", {
                  name: t("common.max_chars"),
                  max: defaultMaxChar,
                  min: Lengths.MIN_3,
                }),
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Input
                title={t("common.max_chars")}
                type="number"
                value={value}
                onChange={(e) => onChange(Number(e.currentTarget?.value))}
                valid={!error}
                errorMessage={error?.message}
                max={defaultMaxChar}
                min={Lengths.MIN_3}
              />
            )}
          />
        ) : null}

        <Flex alignItems="center" justifyContent="space-between">
          <Text>{t("common.required")}</Text>

          <Controller
            key={`inputs.${inputIndex}.settings.required`}
            control={control}
            name={`inputs.${inputIndex}.settings.required`}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Switch onChange={onChange} checked={value} />
            )}
          />
        </Flex>

        {isRadio || isSelect ? (
          <Flex flexDirection="column" gap="s">
            <Text>{t("common.options")}</Text>

            <Controller
              control={control}
              name="optionName"
              rules={{
                minLength: {
                  value: 1,
                  message: t("forms.create.step.2.label.short"),
                },
                maxLength: {
                  value: Lengths.NAME,
                  message: "",
                },
                validate: (v) => validateOptionName(options, t, v),
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Flex alignItems="center" gap="m">
                  <Input
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message}
                    maxLength={Lengths.NAME}
                    onKeyDown={onOptionKeyDown}
                    valid={!error}
                  />

                  <StyledButton
                    onClick={addOption}
                    icon={faPlus}
                    iconWidth="xl"
                    disabled={
                      (optionName && options.includes(optionName)) ||
                      options.length >= 15 ||
                      !optionName.trim().length
                    }
                  />
                </Flex>
              )}
            />

            <Flex flexDirection="column" gap="s">
              {options.map((option, index) => (
                <Flex
                  key={index}
                  alignItems="center"
                  justifyContent="space-between"
                  gap="s"
                >
                  <Controller
                    control={control}
                    name={`inputs.${inputIndex}.options.${index}`}
                    defaultValue={option}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                      <Flex alignItems="center" gap="s" flex={1}>
                        <AtiraIcon
                          icon={isSelect ? faCircle : faDotCircle}
                          size="xs"
                          color="main"
                        />
                        <OptionInput
                          ref={(el) => {
                            optionRefs.current[index] = el!;
                          }}
                          value={value}
                          onChange={onChange}
                          readOnly={editingOptionIndex !== index}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && value?.trim()) {
                              setEditingOptionIndex(null);
                            }
                          }}
                        />

                        <SaveOptionButton
                          onClick={() =>
                            editingOptionIndex !== index
                              ? handleEditOptionClick(
                                  index,
                                  setEditingOptionIndex,
                                )
                              : value?.trim() && setEditingOptionIndex(null)
                          }
                          icon={editingOptionIndex !== index ? faPen : faSave}
                          iconWidth="lg"
                          disabled={
                            editingOptionIndex === index && !value?.trim()
                          }
                        />
                      </Flex>
                    )}
                  />

                  <StyledButton
                    onClick={() => removeOption(index)}
                    icon={faTrash}
                    color="danger"
                    iconWidth="lg"
                  />

                  <Tooltip title={t("common.defaultSetAs")} placement="right">
                    <StyledButtonWithOpacity
                      onClick={() => setOptionAsDefault(index)}
                      icon={faCheck}
                      color="main"
                      iconWidth="lg"
                      $selected={
                        currentInput?.selectInputConfig?.defaultOptionIndex ===
                        index
                      }
                    />
                  </Tooltip>
                </Flex>
              ))}
            </Flex>
          </Flex>
        ) : null}

        {isDate ? <InputConfigDate inputIndex={inputIndex} /> : null}

        <SaveButton onClick={onLocalClose}>
          {t("common.saveAndClose")}
        </SaveButton>
      </Flex>
    </Modal>
  );
};
